import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import PropTypes from "prop-types";
import { useMemo, useRef } from "react";

import RequestActionCenter from "./RequestActionCenter";
import RequestCustomerActionAlert from "./RequestCustomerActionAlert";
import RequestEstimatedIncomeCard from "./RequestEstimatedIncomeCard";
import RequestExpertContactCard from "./RequestExpertContactCard";
import RequestScheduleCard from "./RequestScheduleCard";
import RequestStatusHeroCard from "./RequestStatusHeroCard";
import RequestWorkflowTracker from "./RequestWorkflowTracker";
import { buildCustomerTrackingModel } from "./requestTrackingModel";
import { TRACKING_PAGE_BG } from "./trackingStyles";

const CustomerRequestTrackingLayout = ({
  data,
  steps,
  activeStep,
  shownStep,
  onOpenContractorModal,
  onOpenPaymentModal,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const actionCenterRef = useRef(null);

  const requestPayload = useMemo(
    () => data?.request || data?.Request || null,
    [data?.request, data?.Request]
  );

  const model = useMemo(
    () =>
      buildCustomerTrackingModel({
        request: requestPayload,
        stepList: data?.stepList || data?.StepList || steps,
        trackingMilestones:
          data?.trackingMilestones || data?.TrackingMilestones || [],
        parallelTrackingMilestones:
          data?.parallelTrackingMilestones || data?.ParallelTrackingMilestones || [],
        parallelInsertAfterMainIndex:
          data?.parallelInsertAfterMainIndex ??
          data?.ParallelInsertAfterMainIndex ??
          1,
        activeTrackingMilestoneIndex:
          data?.activeTrackingMilestoneIndex ??
          data?.ActiveTrackingMilestoneIndex ??
          0,
        activeStepIndex: activeStep,
        shownStepIndex: shownStep,
      }),
    [
      requestPayload,
      data?.stepList,
      data?.StepList,
      data?.trackingMilestones,
      data?.TrackingMilestones,
      data?.parallelTrackingMilestones,
      data?.ParallelTrackingMilestones,
      data?.parallelInsertAfterMainIndex,
      data?.ParallelInsertAfterMainIndex,
      data?.activeTrackingMilestoneIndex,
      data?.ActiveTrackingMilestoneIndex,
      steps,
      activeStep,
      shownStep,
    ]
  );

  const handleTaskAction = (task) => {
    if (task.fieldKey === "contractorSelector") {
      onOpenContractorModal?.();
      return;
    }
    if (task.fieldKey === "paymentSelector") {
      onOpenPaymentModal?.();
    }
  };

  const scrollToActionCenter = () => {
    actionCenterRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const actionAlert = model.showActionCenter ? (
    <RequestCustomerActionAlert
      tasks={model.customerTasks}
      onScrollToForm={scrollToActionCenter}
    />
  ) : null;

  const hero = <RequestStatusHeroCard model={model} isMobile={isMobile} />;

  const expertContact = isMobile ? (
    <RequestExpertContactCard model={model} />
  ) : null;

  const actionCenter = model.showActionCenter ? (
    <Box ref={actionCenterRef}>
      <RequestActionCenter
        tasks={model.customerTasks}
        onTaskAction={handleTaskAction}
        onScrollToForm={scrollToActionCenter}
      />
    </Box>
  ) : null;

  const timeline = (
    <RequestWorkflowTracker
      timelineSteps={model.timelineSteps}
      parallelTimelineSteps={model.parallelTimelineSteps}
      parallelInsertAfterMainIndex={model.parallelInsertAfterMainIndex}
      activeIndex={model.activeTimelineIndex}
      currentStageTitle={model.currentStageTitle}
      currentStageDescription={model.currentStageDescription}
    />
  );

  const scheduleAndActions = (
    <Grid container spacing={{ xs: 2, md: 2.5 }} alignItems="stretch">
      {model.showActionCenter ? (
        <Grid item xs={12} md={6}>
          {actionCenter}
        </Grid>
      ) : null}
      <Grid item xs={12} md={model.showActionCenter ? 6 : 12}>
        <RequestScheduleCard schedule={model.schedule} />
      </Grid>
    </Grid>
  );

  const income = <RequestEstimatedIncomeCard income={model.income} />;

  return (
    <Box
      sx={{
        bgcolor: TRACKING_PAGE_BG,
        borderRadius: 2,
        p: { xs: 0, md: 0.5 },
        mx: { xs: -0.5, sm: 0 },
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      <Grid container spacing={{ xs: 2, md: 2.5 }}>
        {isMobile ? (
          <>
            {actionAlert ? (
              <Grid item xs={12}>
                {actionAlert}
              </Grid>
            ) : null}
            <Grid item xs={12}>
              {hero}
            </Grid>
            <Grid item xs={12}>
              {expertContact}
            </Grid>
            {model.showActionCenter ? (
              <Grid item xs={12}>
                {actionCenter}
              </Grid>
            ) : null}
            <Grid item xs={12}>
              {timeline}
            </Grid>
            <Grid item xs={12}>
              <RequestScheduleCard schedule={model.schedule} />
            </Grid>
            <Grid item xs={12}>
              {income}
            </Grid>
          </>
        ) : (
          <>
            {actionAlert ? (
              <Grid item xs={12}>
                {actionAlert}
              </Grid>
            ) : null}
            <Grid item xs={12}>
              {hero}
            </Grid>
            <Grid item xs={12}>
              {timeline}
            </Grid>
            <Grid item xs={12}>
              {scheduleAndActions}
            </Grid>
            <Grid item xs={12}>
              {income}
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

CustomerRequestTrackingLayout.propTypes = {
  data: PropTypes.object,
  steps: PropTypes.array,
  activeStep: PropTypes.number,
  shownStep: PropTypes.number,
  onOpenContractorModal: PropTypes.func,
  onOpenPaymentModal: PropTypes.func,
};

export default CustomerRequestTrackingLayout;
